import { useEffect } from 'react';
import './SearingShotAnimation.css';

/**
 * PROFESSIONAL SEARING SHOT ANIMATION
 * Fire-type special move - fiery explosion that radiates outward
 * 
 * Key Features:
 * - Fire-type special attack (100 base power, 100% accuracy)
 * - 30% chance to burn
 * - Victini signature move with radiant fire orb explosion
 * - Professional gaming quality with intense heat wave effects
 */
export default function SearingShotAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;

    const elements = [];

    // PHASE 1: Heat Energy Gathering (0-500ms)
    playerEl.classList.add('searing-shot-user-charge');

    const heatAura = document.createElement('div');
    heatAura.className = 'searing-shot-heat-aura';
    heatAura.style.left = `${playerX - 80}px`;
    heatAura.style.top = `${playerY - 80}px`;
    container.appendChild(heatAura);
    elements.push(heatAura);

    // Orbiting heat particles
    for (let i = 0; i < 12; i++) {
      const heat = document.createElement('div');
      heat.className = 'searing-shot-heat-particle';
      const angle = (i / 12) * 360;
      const radius = 60 + (i % 3) * 10;
      heat.style.left = `${playerX + Math.cos(angle * Math.PI / 180) * radius - 5}px`;
      heat.style.top = `${playerY + Math.sin(angle * Math.PI / 180) * radius - 5}px`;
      heat.style.animationDelay = `${i * 35}ms`;
      heat.style.setProperty('--target-x', `${playerX - (playerX + Math.cos(angle * Math.PI / 180) * radius)}px`);
      heat.style.setProperty('--target-y', `${playerY - (playerY + Math.sin(angle * Math.PI / 180) * radius)}px`);
      container.appendChild(heat);
      elements.push(heat);
    }

    // PHASE 2: Searing Orb Launch (500-1000ms)
    const phase2Timer = setTimeout(() => {
      // Create searing orb projectile
      const orb = document.createElement('div');
      orb.className = 'searing-shot-orb';
      orb.style.left = `${playerX}px`;
      orb.style.top = `${playerY}px`;
      orb.style.setProperty('--dx', `${dx}px`);
      orb.style.setProperty('--dy', `${dy}px`);
      container.appendChild(orb);
      elements.push(orb);

      // Heat distortion along path
      for (let i = 0; i < 14; i++) {
        const distortTimer = setTimeout(() => {
          const distort = document.createElement('div');
          distort.className = 'searing-shot-distortion';
          const progress = i / 14;
          distort.style.left = `${playerX + dx * progress + (Math.random() - 0.5) * 20 - 10}px`;
          distort.style.top = `${playerY + dy * progress + (Math.random() - 0.5) * 20 - 10}px`;
          container.appendChild(distort);
          elements.push(distort);
        }, i * 30);
        elements.push({ timer: distortTimer, type: 'timer' });
      }

      // Trailing embers
      for (let i = 0; i < 18; i++) {
        const emberTimer = setTimeout(() => {
          const ember = document.createElement('div');
          ember.className = 'searing-shot-trail-ember';
          const progress = i / 18;
          ember.style.left = `${playerX + dx * progress + (Math.random() - 0.5) * 30 - 4}px`;
          ember.style.top = `${playerY + dy * progress + (Math.random() - 0.5) * 30 - 4}px`;
          container.appendChild(ember);
          elements.push(ember);
        }, i * 24);
        elements.push({ timer: emberTimer, type: 'timer' });
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Radiant Explosion Impact (1000-1600ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('searing-shot-opponent-hit');

      // Massive radiant explosion
      const explosion = document.createElement('div');
      explosion.className = 'searing-shot-explosion';
      explosion.style.left = `${opponentX - 120}px`;
      explosion.style.top = `${opponentY - 120}px`;
      container.appendChild(explosion);
      elements.push(explosion);

      // Radiating fire rings
      for (let i = 0; i < 4; i++) {
        const ringTimer = setTimeout(() => {
          const ring = document.createElement('div');
          ring.className = 'searing-shot-fire-ring';
          ring.style.left = `${opponentX - 80}px`;
          ring.style.top = `${opponentY - 80}px`;
          container.appendChild(ring);
          elements.push(ring);
        }, i * 100);
        elements.push({ timer: ringTimer, type: 'timer' });
      }

      // Searing burst particles in all directions
      for (let i = 0; i < 24; i++) {
        const burstAngle = (i / 24) * 360;
        const burstDist = 70 + Math.random() * 60;
        const burst = document.createElement('div');
        burst.className = 'searing-shot-burst';
        burst.style.left = `${opponentX - 5}px`;
        burst.style.top = `${opponentY - 5}px`;
        burst.style.setProperty('--scatter-x', `${Math.cos(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.setProperty('--scatter-y', `${Math.sin(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.animationDelay = `${i * 12}ms`;
        container.appendChild(burst);
        elements.push(burst);
      }

      // Heat shimmer effect over area
      const shimmer = document.createElement('div');
      shimmer.className = 'searing-shot-shimmer';
      shimmer.style.left = `${opponentX - 100}px`;
      shimmer.style.top = `${opponentY - 100}px`;
      container.appendChild(shimmer);
      elements.push(shimmer);
    }, 1000);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Burn Aftermath (1600-2000ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('searing-shot-user-charge');

      for (let i = 0; i < 8; i++) {
        const cinder = document.createElement('div');
        cinder.className = 'searing-shot-cinder';
        cinder.style.left = `${opponentX + (Math.random() - 0.5) * 100 - 6}px`;
        cinder.style.top = `${opponentY + (Math.random() - 0.5) * 60 - 6}px`;
        cinder.style.animationDelay = `${i * 40}ms`;
        container.appendChild(cinder);
        elements.push(cinder);
      }
    }, 1600);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (2000ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('searing-shot-user-charge');
      opponentEl.classList.remove('searing-shot-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 2000);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('searing-shot-user-charge');
      opponentEl.classList.remove('searing-shot-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

SearingShotAnimation.moveType = 'fire';
